/** @module SiteAdmin/AttributeForm */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-style",
	"dojo/store/Memory",
	"dojo/when",
	"csi",
	"csi/_base/underscore",
	"csi/action/Popup",
	"csi/action/PopupController",
	"csi/comm",
	"csi/core/extendedPathService",
	"csi/def",
	"csi/def/Field",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/enums",
	"csi/form/Widget",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/reflection",
	"csi/reflection/reflectionConstants",
	"csi/resolver",
	"csi/middleware/rollup/rollupService",
	"csi/service/nodeService",
	"csi/store/Names",
	"csi/store/_base",
	"csi/types",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/Search",
	"SiteAdmin/BusinessObject",
	"SiteAdmin/Locale",
	"src/c8-platform/type/uom"
], function(array, declare, lang, style, Memory, when, csi, _, Popup, PopupController, comm, {parseExtendedPath}, def, Field, Relationship, ViewRef, enums, Widget, heading, html, locale, reflection, {URL_ENUM}, resolver, {validateValue}, {resolveNode, resolveNodes},
	Names, csiStore, types, xml, Query, Search, BusinessObject, Locale, {CLASS_UNIT_OF_MEASURE, CLASS_UOM_CONVERSION, CLASS_UOM_MODULE, CLASS_UOM_TYPE, generateUnitOfMeasureURL, generateUOMTypeURL, getUOMConversionString, getUOMNameAndUOMTypeNameFromConversionCNL, getUOMValueString, UOM_SUFFIX}) {

	const COPY_CATEGORY = "CopyCategory";
	const COPY_CATEGORY_ALL = "All";
	const COPY_ROOT_TYPE = "CopyRootType";
	const DESTINATION_CLASS_NAMES_WITH_SUBTYPE = {
		"SpecificationItemDefinition": "SpecificationDataSheetSubtype",
		"LookupItem": "LookupItemSubtype"
	};

	const FIELD_FLAG_READ_ONLY = "FlagReadOnly";
	const FIELD_LOCALE_VALIDATION = "LocaleValidation";
	const FIELD_REQUIRED = "Required";
	const FIELD_UNIT_OF_MEASURE_ENABLED = "UnitOfMeasureEnabled";
	const FIELD_UNIT_OF_MEASURE = "UnitOfMeasure";
	const FIELD_UOM_TYPE = "UOMType";
	const FIELD_VALIDATION_EXPRESSION = "ValidationExpression";

	const TYPES = [
		xml.ATTR_BOOLEAN,
		xml.ATTR_BOOLEAN + xml.ATTR_MAP,
		xml.ATTR_BOOLEAN + xml.ATTR_LIST,
		xml.ATTR_BOOLEAN + xml.ATTR_VECTOR,
		xml.ATTR_DOUBLE,
		xml.ATTR_DOUBLE + xml.ATTR_MAP,
		xml.ATTR_DOUBLE + xml.ATTR_LIST,
		xml.ATTR_DOUBLE + xml.ATTR_VECTOR,
		xml.ATTR_ENUM,
		xml.ATTR_ENUM + xml.ATTR_MAP,
		xml.ATTR_ENUM + xml.ATTR_LIST,
		xml.ATTR_ENUM + xml.ATTR_VECTOR,
		xml.ATTR_INTEGER,
		xml.ATTR_INTEGER + xml.ATTR_MAP,
		xml.ATTR_INTEGER + xml.ATTR_LIST,
		xml.ATTR_INTEGER + xml.ATTR_VECTOR,
		xml.ATTR_INTEGER + xml.ATTR_SET,
		xml.ATTR_STRING,
		xml.ATTR_STRING + xml.ATTR_MAP,
		xml.ATTR_STRING + xml.ATTR_LIST,
		xml.ATTR_STRING + xml.ATTR_VECTOR,
		xml.ATTR_STRING + xml.ATTR_SET,
		xml.ATTR_TIME,
		xml.ATTR_TIME + xml.ATTR_MAP,
		xml.ATTR_TIME + xml.ATTR_LIST,
		xml.ATTR_TIME + xml.ATTR_VECTOR,
		xml.ATTR_URL,
		xml.ATTR_REF,
		xml.ATTR_REF + xml.ATTR_MAP,
		xml.ATTR_REF + xml.ATTR_LIST,
		xml.ATTR_REF + xml.ATTR_VECTOR,
		xml.ATTR_REF + xml.ATTR_SET
	];

	const NO_VALUE = [
		{type: xml.ATTR_REF},
		{type: xml.ATTR_URL},
		{type: xml.ATTR_STRING, format: types.FORMAT_COMMENT},
		{type: xml.ATTR_STRING, format: types.FORMAT_COMMENT_LATEST},
		{type: xml.ATTR_STRING, format: types.FORMAT_BARCODE},
		{type: xml.ATTR_STRING, format: types.FORMAT_RICH},
		{type: xml.ATTR_STRING, format: types.FORMAT_URL}
	];

	const COLUMNWIDTH_TYPE = "ColumnWidthType";

	function getTargetClassNamesForRef() {
		// summary: get class names for the drop-down.
		var rcs = reflection.getClasses();
		var clsNames = _.map(rcs, function(rc) {return rc.getType();});
		clsNames = _.filter(clsNames, function(name) {
			return name.charAt(0) != '_' && !isUOMClass(name);
		});
		return clsNames;
	}

	/**
	 * Return true if class name is a UOM class 
	 * @param {String} name 
	 * @return {boolean}
	 */
	function isUOMClass(/*String*/name) {
		return name === CLASS_UNIT_OF_MEASURE ||
			name === CLASS_UOM_CONVERSION ||
			name === CLASS_UOM_MODULE ||
			name === CLASS_UOM_TYPE;
	}

	// cache of the information about subtyped enums
	let gSubtypesEnums = null;

	function getSubtypesEnums() {
		if (gSubtypesEnums)
			return gSubtypesEnums;

		gSubtypesEnums = {
			"StyleSubtype": {
				configurableAttributeUrl: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
				allowedPaths: [
					"(Style)",
					"(StyleAttributes)Child:__Parent__(Style)"
				]
			},
			"MaterialSubtype": {
				configurableAttributeUrl: "centric://REFLECTION/BuildInAttribute/Product/ProductType",
				allowedPaths: [
					"(Material)",
					"(MaterialAttributes)Child:__Parent__(Material)"
				]
			}
		};

		// add all concrete subtyped classes which have corresponding enumeration
		var rcSubtypedClass = reflection.getClass("SubtypedClass");
		if (rcSubtypedClass) {
			_.forEach(rcSubtypedClass.getConcreteClasses(), function(rc) {
				var className = rc.getType();
				var subtypeName = className + "Subtype";

				// do we have a corresponding Subtype enum?
				var enumList = reflection.getEnumList(subtypeName);
				if (!enumList)
					return;

				// does the class have the corresponding Business Object?
				var alias = rc.getAlias();
				if (!alias)
					return;

				var info = {
					configurableAttributeUrl: "centric://REFLECTION/BuildInAttribute/" + className + "/Subtype",
					allowedPaths: [
						"(" + className + ")"
					]
				};

				if (rc.isMaster()) {
					info.allowedPaths.push("(" + rc.getRevisionClass().getType() + ")Child:__Parent__(" + className + ")");
				}

				gSubtypesEnums[subtypeName] = info;
			});
		}

		return gSubtypesEnums;
	}

	function hasSubtype(fieldTargetClassValue) {
		return fieldTargetClassValue in DESTINATION_CLASS_NAMES_WITH_SUBTYPE;
	}

	function isBuiltInAttribute(node) {
		return node && node.$Type != "CustomAttribute";
	}

	function getConfigurableAttributeFromBOByName(/*BusinessObject*/bo, /*String*/name, /*Cache*/cache) {
		var ca = null;
		var attrs = bo.ConfigurableAttributes;
		if (attrs) {
			var caUrl = attrs[name];
			if (!caUrl) {
				_.some(bo.RelatedClassNames, function(clsName) {
					caUrl = attrs[clsName + "/" + name];
					return caUrl;
				});
			}

			if (caUrl) {
				ca = cache.getNode(caUrl, true);
				if (!ca) {
					console.error("Cannot load the ConfigurableAttribute URL=", caUrl);
				}
			}
		}
		return ca;
	}

	function getDisplayableClassName(clsName) {
		return locale.type(clsName) + " (" + clsName + ")";
	}

	function getDerivedBusinessObjects(/*BusinessObject*/bo, /*Cache*/cache) {
		// summary: given the business object, return business objects from all derived classes
		const setUrls = new Set();

		bo.RelatedClassNames.forEach((clsName) => {
			const currentRC = reflection.getClass(clsName);
			currentRC.traverse(false, true, (rc) => {

				if (rc == currentRC) {
					return;
				}

				const boUrl = rc.getBusinessObject();

				if (boUrl) {
					setUrls.add(boUrl);
				}

			});
		});

		const bos = cache.getNodes([...setUrls]);
		return bos;
	}

	function validateAttributeIdAgainstBusinessObject(/*BusinessObject*/nodeBO, /*String*/attrId) {
		// summary: throw exception if an attribute with 'attrId' exists in business object
		var attrs = nodeBO.ConfigurableAttributes;
		if (attrs) {
			var caUrl = attrs[attrId] || attrs[nodeBO.RootClassName + "/" + attrId];
			if (caUrl) {
				throw new Error(locale.resource("CustomAttribute_ErrorDuplicate", [attrId, nodeBO.$Name]));
			}
		}
	}

	function validateAttributeIdAgainstReflectionClass(/*Class*/rc, /*String*/attrId) {
		// summary: throw exception if an attribute with 'attrId' exists in reflection class
		var attr = rc.getAttribute(attrId, false, false, true);
		if (attr) {
			throw new Error(locale.resource("CustomAttribute_ErrorDuplicate", [attrId, rc.getType()]));
		}
	}

	// Object{Class[]}: the map of reflection classes to validate attribute Id with the key as a root reflection class name
	const _reflectionClassesForAttributeIdValidationMap = {};

	function getReflectionClassesForAttributeIdValidation(/*String*/rootClassName) {
		var reflectionClasses = _reflectionClassesForAttributeIdValidationMap[rootClassName];

		if (reflectionClasses == null) {

			reflectionClasses = [];

			// go to a domain class of root class
			var rootClass = reflection.getClass(rootClassName);
			var domainClass = rootClass.domainClass;
			if (domainClass != null) {
				// Get reflection classes from domain class excluding the root class of business object.
				// We exclude a root class for now to avoid collecting base classes of root class because
				// we need to allow to have an attribute with the same Id as on a base class of root class
				var reflectionClassesFromDomainClass = _.values(domainClass.secondaries);
				reflectionClassesFromDomainClass.push(domainClass.primary);
				reflectionClassesFromDomainClass = _.without(reflectionClassesFromDomainClass, rootClass);

				// get all reflection classes including base ones
				function reflectionClassCollector(rc) {
					reflectionClasses.push(rc);
				}
				_.each(reflectionClassesFromDomainClass, function(rc) {
					rc.traverse(true, false, reflectionClassCollector);
				});

				// add a root class back to validate against it
				reflectionClasses.push(rootClass);
				reflectionClasses = _.uniq(reflectionClasses);
			}

			_reflectionClassesForAttributeIdValidationMap[rootClassName] = reflectionClasses;
		}

		return reflectionClasses;
	}

	const _PopupController = declare(PopupController, {
		getFormData: function() {
			// summary: allow editing in popup view
			var fd = this.inherited(arguments);
			fd.m_bEditable = true;
			return fd;
		},
		/**
		  * @override
		  * preselect default enum type
		*/
		_onCreatePopup: function(view) {
			const field = this.widget.getField(reflection.TYPE_ENUM);
			const store = field.getStore();
			const value = field.getValue();
			const item = store.get(value);
			// if subtype value is not valid, item will be undefined
			if (item) {
				const name = item[store.idProperty];
				const url = URL_ENUM + name;
				view.urlPreSelect = url;
			}
			this.inherited(arguments);
		},
		/**
		  * @override
		  * always show all results, allow preselecting default enum type
		*/
		_onCreatePopupParams: function(viewParams) {
			viewParams.querySize = 0;
			viewParams.querySizeFixed = true;
			this.inherited(arguments);
		}
	});

	return declare(Widget, {

		// ignore default save & new behavior
		bDefaultSaveAndNew: false,

		"class": "csi-form-view-block",
		DEPENDS_ON: "DependsOn",

		/**@property {boolean} initializedFormat - indicate if the Format Field initialized or not in the case of edit mode */
		initializedFormat: false,

		/**@property {string} defaultInitialValue - the value of the initial value for the type*/
		defaultInitialValue: null,

		/** @property {string} dependsOnEnumListName - the name of DependsOn EnumList node if applicable */
		dependsOnEnumListName: null,

		/** @property {boolean} uomForm - are we displaying the uom attributes form */
		uomForm: false,

		setBO: function(/*Node*/boNode) {
			// summary: set business object to determine the parent node in the form that creates new attribute
			// used in selection of owner field in that form
			this.cacheView.cacheNode(boNode);
			this.boURL = boNode.$URL;

			// create a map of classes for attribute Id validation
			this.reflectionClassesForAttributeIdValidationMap = {};
			_.each(boNode.RelatedClassNames, function(className) {
				this.reflectionClassesForAttributeIdValidationMap[className] = getReflectionClassesForAttributeIdValidation(className);
			}, this);
		},

		onViewStart: function(dfds) {
			if (this.isCopy()) {
				this.boURL = this.cacheView.getNode(this.urlRoot).__Parent__;
				const dfd = this.cacheView.getNodeDeferred(this.boURL);
				dfds.push(dfd);
			}
		},

		/**
		 * @override
		 * Populate dependsOnEnumListName in the case of edit to be able to query results for "DependsOn" field
		 */
		_buildView: function() {
			if (this.isEdit()) {
				const attributeNode = this.getViewNode();
				const dependsOnAttributeURL = attributeNode.DependsOn;
				this.dependsOnEnumListName = this.cacheView.getNode(dependsOnAttributeURL)?.$Name;
			}

			this.inherited(arguments);
		},

		getBONode: function() {
			return this.cacheView.getNode(this.boURL);
		},

		/**
		 * Add necessary URLs from domain class of business object
		 * @param {Node} node
		 * @return {Request}
		 */
		_getRootRequest: function(node) {
			var req = this.inherited(arguments);
			var urls = req ? [req.getParameter(comm.QUERY_URL)] : [];

			// preload all BOs from reflection classes
			var boNode = this.getBONode();

			_.each(boNode.RelatedClassNames, function(className) {
				var reflectionClasses = getReflectionClassesForAttributeIdValidation(className);
				_.each(reflectionClasses, function(rc) {
					var v = rc.getBusinessObject();
					if (xml.isValidURL(v)) {
						urls.push(v);
					}
				});
			});

			urls = _.uniq(urls);

			if (urls.length) {
				if (req) {
					req.addParameter(comm.OP_URL, urls, true);
				} else {
					req = comm.getNodeRequest(urls, null, true);
				}
			}

			return req;
		},

		getSourceRootNode: function() {
			return this.cacheView.getNode(this.getSourceView().urlRoot);
		},

		getFieldStore: function(/*Field*/field, /*String*/value) {
			// summary: return custom stores for various fields
			var node = this.getFormSourceNode();
			var attrType = this.getFieldValue(reflection.ATTR_VALUETYPE);
			var name = field.getControlName();
			var data;

			if (name === reflection.ATTR_VALUE) {
				// we can't use enum aggregate stores here since Initial Value is a string attribute, so query EnumList directly
				if (attrType === xml.ATTR_ENUM) {
					const enumId = this.getFieldValue(reflection.TYPE_ENUM);
					return enums.getServerStore(enumId);
				}
			} else if (name == reflection.ATTR_VALUETYPE) {
				// collect list of available attribute types
				if (this.isFieldEditable(field)) {
					var attrTypes = TYPES;

					data = array.map(attrTypes, function(type) {return {name: locale.getAttributeType(type), id: type};});
					data = csi.sort(data, "name");
				} else {
					data = [{name: locale.getAttributeType(value), id: value}];
				}

				return new Memory({data: data});
			} else if (name == BusinessObject.ATTR_TARGET_CLASS_NAME) {
				// collect list of available target classes for 'ref' attributes
				if (this.isFieldEditable(field)) {
					data = array.map(getTargetClassNamesForRef(), function(clsName) {return {name: getDisplayableClassName(clsName), id: clsName};});
					data = csi.sort(data, "name");
				} else {
					data = [{name: locale.type(value), id: value}];
				}

				return new Memory({data: data});
			} else if (name == reflection.ATTR_FORMAT) {
				return this._getFormatStore();
			} else if (name == COPY_ROOT_TYPE) {
				// collect copy category types
				data = [];

				if (node) {
					csi.applyForEach(node[reflection.ATTR_COPY_CATEGORIES], function(v, key) {
						if (key != COPY_CATEGORY_ALL) {
							data.push({name: key, id: locale.type(key)});
						}
					});

					data = csi.sort(data, "name");
				}

				data.unshift({name: locale.resource(COPY_CATEGORY_ALL), id: COPY_CATEGORY_ALL});
				return new Memory({data: data});
			} else if (name == COPY_CATEGORY) {
				// return store for copy category
				var bRef = xml.attributeTypeIsRef(attrType);
				var reflectionAttributeOwner = this.getFieldValue(reflection.ATTR_REFLECTION_ATTR_OWNER);

				if (this.isCopy()) {
					reflectionAttributeOwner = this.getSourceRootNode().AttributeClassName;
				}

				var rc = reflection.getClass(reflectionAttributeOwner);
				var bRev = rc.isRevision();

				return enums.getStore(COPY_CATEGORY, null, function(v) {
					var bGood = bRef || (v != "CopyCategory:Optional-FilteredTypes");

					// remove "Optional" choice for Revision classes (DE18194)
					bGood = bGood && !(bRev && v == "CopyCategory:Optional");

					return bGood;
				});
			} else if (name == reflection.ATTR_REFLECTION_ATTR_OWNER) {
				var ctx = this.getContext();
				var boNode = ctx.cache.getNode(this.boURL);

				if (this.isCopy()) {
					boNode = this.getSourceRootNode();
				}

				// add locale.type(boNode.AttributeClassName) for translation in the future?
				// currently the translation for master and revision is the same,
				// e.g. both Document and DocumentRevision are translated as Document
				data = [{name: boNode.AttributeClassName, id: boNode.AttributeClassName}];
				if (this.isAttributeClassDifferentFromRoot(boNode)) {
					data.push({name: boNode.RootClassName, id: boNode.RootClassName});
				}
				return new Memory({
					data: data,
					getEmptyValue: function() {
						return this.data[0].id;
					}
				});
			} else if (name == this.DEPENDS_ON) {
				const enumId = this.getFieldValue(reflection.TYPE_ENUM);
				const {dependsOnEnumListName} = this;

				if (enumId && dependsOnEnumListName) {
					const ctx = this.getContext();
					const urlRoot = ctx.fd.getParent().$URL;
					ctx.setNodeURL(urlRoot);
					const reflectionAttributeOwner = this.getFieldValue(reflection.ATTR_REFLECTION_ATTR_OWNER);

					const relationship = lang.mixin(new Relationship(), {
						m_id: "ConfigurableAttributes",
						m_query: new Query()
							.Attr("Format", xml.OP_EQUALS, dependsOnEnumListName)
							.Attr("ReflectionAttributeOwner", xml.OP_EQUALS, reflectionAttributeOwner)
					});

					const storeDependsOn = ctx.view.createHeadingNamesStore(ctx, field, value, relationship, null, {
						ignoreInvalid: true,

						// always get the internal name for the enum list
						translate: (node) => node.$Name,
					});

					// add additional possible attributes if DependsOn enumeration represents some Subtype enumeration
					const subtypeEnumInfo = getSubtypesEnums()[dependsOnEnumListName];

					if (subtypeEnumInfo) {
						// check if the current BO is supported
						const dependsOnPath = _.find(subtypeEnumInfo.allowedPaths, (path) => {
							const extPath = parseExtendedPath(path);
							return extPath.getStartType() === reflectionAttributeOwner;
						});

						if (dependsOnPath) {
							const configAttr = this.cacheView.getNode(subtypeEnumInfo.configurableAttributeUrl, true);
							const storeItemObj = {path: dependsOnPath};
							storeItemObj[storeDependsOn.idProperty] = configAttr.$URL;
							storeItemObj[storeDependsOn.nameProperty] = locale.getNodeName(configAttr, this.cacheView);
							storeDependsOn.put(storeItemObj);
						}
					}

					return storeDependsOn;
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * Return store for format field
		 * @return {Memory}
		 */
		_getFormatStore() {
			const attrType = this.getFieldValue(reflection.ATTR_VALUETYPE);
			let data;

			if (enums.isEnumBaseType(attrType)) {
				const enumId = this.getFieldValue(reflection.TYPE_ENUM);
				data = [
					{id: enumId, name: enumId}
				];
			} else {
				// collect list of available formats based on selected attribute type - use double formats for float
				data = array.map(types.getFormats(attrType) || [""], (id) => {
					return {
						id,
						name: types.getLocalizedFormat(id)
					};
				});

				if (attrType && !attrType.indexOf(xml.ATTR_STRING)) {
					data = csi.sort(data, "name");
				}
			}

			return new Memory({
				data,
				labelProperty: "name",
				getEmptyValue: () => data[0].id,
			});
		},

		isAttributeClassDifferentFromRoot: function(boNode) {
			return boNode.RootClassName != boNode.AttributeClassName;
		},

		onChangeCopyRootType: function(formView, field, value) {
			var node = this.getFormSourceNode();
			var copyCategoriesLocked = node ? node[reflection.ATTR_COPY_CATEGORIES_LOCKED] : null;
			var copyCategories = node ? node[reflection.ATTR_COPY_CATEGORIES] : null;
			var copyCategoryValue = (value == COPY_CATEGORY_ALL) ? formView.m_formData.AllCopyCategory : copyCategories[value];
			var fieldCopy = formView.getField(COPY_CATEGORY);

			fieldCopy.m_control.set("value", copyCategoryValue);
			fieldCopy.m_control.set("disabled", copyCategoriesLocked || (value != COPY_CATEGORY_ALL));
		},

		onChangeCopyCategory: function(formView, field, value) {
			// Can be changed for 'All' root type only (otherwise the control is disabled)!
			formView.m_formData.AllCopyCategory = value;
		},

		/**
		 * update visibility and values of enum/format fields, make the target class field required as needed
		 */
		onChangeTargetClass: function(/*def.View*/defView, /*Field*/field, /*Any*/value) {
			this._updateTargetClassAndSubtype();

			// for image,  is always calculated dynamically based on the chosen image size.
			const isImage = (value == reflection.TYPE_IMAGE);
			this.setFieldHidden(reflection.ATTR_COLUMN_WIDTH, isImage);

			// Make subtype required for ref with subtype
			const isParentRef = xml.attributeTypeIsRef(this.getFieldValue(reflection.ATTR_VALUETYPE));
			const bSubtype = isParentRef && hasSubtype(this.getFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME));
			this.setFieldRequired(reflection.ATTR_SUBTYPE, bSubtype);

			if (isImage) {
				this.setFieldValue(reflection.ATTR_COLUMN_WIDTH, xml.enumCreate(COLUMNWIDTH_TYPE, ""));
			}
		},

		/** Reset depends on store */
		_resetDependsOn() {
			const fieldDependsOn = this.getField(this.DEPENDS_ON);
			this.resetFieldStoreAndValue(fieldDependsOn, "");
			this.setFieldValue("AutofillDependentEnum", false)
		},

		/**
		 * On change handler for subtype
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 */
		onChangeSubtype(defView, field, value) {
			this._resetDependsOn();
			this._drawDependsOn();
		},

		/**
		 * Return the EnumList name to query the results for the "DependsOn" field
		 * @param {String} enumId 
		 * @returns {String}
		 */
		async _getDependsOnEnumListName(enumId) {
			const search = new Search().byType(reflection.TYPE_ENUM);
			search.getQuery().Node(xml.PROP_NAME, xml.OP_EQUALS, enumId);
			const response = await search.execute();
			const enumNode = response.getResultNode();
			let dependsOnEnumListName = "";

			if (enumNode) {
				// EnumList URL the selected enumeration depends on
				const urlDependsOn = enumNode.DependsOn;
				const bDependsOn = urlDependsOn !== csi.EMPTY_URL;

				if (bDependsOn) {
					// name of EnumList this attribute depends on
					const cache = this.cacheView;
					let dependsOnEnumList = cache.getNode(urlDependsOn);

					if (!dependsOnEnumList) {
						dependsOnEnumList = await resolveNode(urlDependsOn, cache);
					}

					dependsOnEnumListName = dependsOnEnumList.$Name;
				}
			}

			return dependsOnEnumListName;
		},

		async onChangeEnum(/*def.View*/defView, /*Field*/field, /*Any*/value) {
			// update initial value and format for selected enum
			this.resetFieldStoreAndValue(reflection.ATTR_FORMAT);
			this._updateInitialValue(true);
			const enumId = value;

			if (!!enumId) {
				this.dependsOnEnumListName = await this._getDependsOnEnumListName(enumId);
			}

			this._resetDependsOn();
			this._drawDependsOn();
		},

		onChangeFlagReadOnly: function(defView, field, value) {
			this._drawRFlagRestrictDragEdits();
		},

		onChangeFlagHidden: function(/*def.View*/defView, /*Field*/field, /*Any*/value) {
			this._updateViableMatrixVisibility();
		},

		onChangeDependsOn: function(/*def.View*/defView, /*Field*/field, /*Any*/value) {
			this._disableInitialValue(value);

			// set DependsOnPath from the selected item
			var item = field.m_control.get("item");
			this.setFieldValue("DependsOnPath", item.path || "");
		},

		onDrawDependsOnPath: function(formView, field, value, eParent, eControl) {
			// summary: hide the row with the field, but not the field itself
			var tr = this.getFieldRow(field);
			html.setVisible(tr, false);
		},

		onChangeFormat: function(formView, field, value) {
			// summary: initialize the initial value field
			this._updateInitialValue(false);

			if (this.bNewFrom || this.initializedFormat) {
				// in the case of edit mode, just use the value from the edited node during initialization
				this._updateColumnWidth(null, value);
			}

			this.initializedFormat = true;
		},

		onChangeType: function(formView, field, value) {
			// summary: update visibility and values of enum/format fields
			this.resetFieldStoreAndValue(reflection.ATTR_FORMAT);
			this._updateInitialValue(true);
			this._updateTargetClassAndSubtype();
			this._updateViableMatrixVisibility();
			this._updateColumnWidth(value, null);
		},

		onChangeUOMType: async function(formView, field, value) {
			// populate the UnitOfMeasure store when a UOMType is selected for the new action
			if (!this.isEdit()) {
				const fieldUOM = this.getField(FIELD_UNIT_OF_MEASURE);
				const uomType = await resolveNode(this.getFieldValue(FIELD_UOM_TYPE));
				const uoms = await resolveNodes(uomType.UOMs);
				const store = Names.createFromNodes(uoms, {bSort: false});

				this.setFieldStore(fieldUOM, store);
				this.setFieldValue(fieldUOM, csi.EMPTY_STRING);
			}
		},

		/**
		 * Enable UnitOfMeasureEnabled field
		 */
		_enableUOM() {
			const type = this.getFieldValue(reflection.ATTR_VALUETYPE);
			const isDouble = type === xml.ATTR_DOUBLE;
			// hide UOM checkbox if in edit form for a non uom attribute
			this.setFieldHidden(FIELD_UNIT_OF_MEASURE_ENABLED, !isDouble || (this.isEdit() && !this.getFieldValue(FIELD_UNIT_OF_MEASURE_ENABLED)));
			if (!isDouble) {
				this.setFieldValue(FIELD_UNIT_OF_MEASURE_ENABLED, false);
			}
		},

		/**
		 * Populate the "ColumnWidth" field by default value if configured, otherwise, default to "S"
		 * @param {string} [type]
		 * @param {string} [format]
		 */
		_updateColumnWidth: function(type, format) {
			// if the selected format doesn't find an entry, then set the value by the type
			type = type || this.getFieldValue(reflection.ATTR_VALUETYPE);
			const attributeID = this.getFieldValue(xml.ATTR_NAME);

			const defaultWidthEnum = types.getAttributeDefaultColumnWidth(attributeID, type, format);
			this.setFieldValue(reflection.ATTR_COLUMN_WIDTH, defaultWidthEnum);
		},

		onDrawDependsOn: function(formView, field, value) {
			// summary: update visibility and values of enum/format fields
			this._drawDependsOn();
		},

		onDrawCopyOptionName: function(formView, field, value, eParent, eControl) {
			var node = this.getFormSourceNode();
			if (node && node[reflection.ATTR_COPY_CATEGORIES_LOCKED]) {
				html.setDisabled(eControl, true);
			}
		},

		onDrawFlagReadOnly: function(formView, field, value, eParent, eControl) {
			this._drawFlags(eControl, xml.ATTR_FLAG_NOEDIT);
		},

		onDrawFlagViableMatrix: function(formView, field, value, eParent, eControl) {
			this._updateViableMatrixVisibility();
		},

		onDrawFormat: function(formView, field, value, eParent, eControl) {
			// modify the initial value field for the first time
			// TODO - why not do this before starting to render form?
			this._updateInitialValue(false);
		},

		/**
		 * @override
		 * Need to handle Initial Value properly as it is marshalled from JSON
		 */
		getValueForField: function(field, node) {
			let baseVal = this.inherited(arguments);
			if (baseVal && field.m_id == reflection.ATTR_VALUE) {
				if (this.defaultInitialValue) {
					baseVal = this.defaultInitialValue;
				}
				if (typeof baseVal === "string") {
					baseVal = JSON.parse(baseVal);
				}
			}
			return baseVal;
		},

		onDrawInitialValue: function(formView, field, value, eParent, eControl) {
			// special handling for enumerated types
			const type = this.getFieldValue(reflection.ATTR_VALUETYPE);
			if (type == xml.ATTR_ENUM) {
				this._updateInitialValueEnumValue(value);
			} else {
				const w = this.getFieldControl(reflection.ATTR_VALUE);
				if (w && w.set) {
					if (type == xml.ATTR_TIME) {
						value = value ? new Date(value).getTime() : 0;
					}
					w.set("value", value);
				}
			}

			if (enums.isEnumBaseType(type)) {
				var node = this.getFormSourceNode();
				if (isBuiltInAttribute(node)) {
					//If EnumList is Internal then initial value shouldn't be editable on Built-in attributes
					var enumList = reflection.getEnumList(node[reflection.ATTR_FORMAT]);
					eControl.set("disabled", enumList.internal);
				}
			}
		},

		onDrawRFlagDisableClientExpression: function(formView, field, value, eParent, eControl) {
			//cannot use _drawRFlags, because the checkox is set when flag is not set, e.g.
			//flag is for Disable Client Expression and checkbox is Enable Client Expression
			var node = this.getFormSourceNode();
			var disableClientExp = node ? node[reflection.ATTR_EXP_ONCLIENT_DISABLED] : false;
			eControl.set("value", !disableClientExp);
		},

		onDrawRFlagRestrictDragEdits: function(formView, field, value, eParent, eControl) {
			this._drawRFlagRestrictDragEdits();
		},

		onDrawReflectionAttributeOwner: function(formView, field, value, eParent, eControl) {
			var boNode = this.getBONode();

			var fieldReflectionAttributeOwner = this.getField(reflection.ATTR_REFLECTION_ATTR_OWNER);
			var tr = this.getFieldRow(fieldReflectionAttributeOwner);
			html.setVisible(tr, this.isAttributeClassDifferentFromRoot(boNode));
		},

		onDrawType: function(formView, field, value, td, control) {
			// summary: set string as default value type
			if (control) {
				style.set(control.domNode, "width", "150px");
			}
		},

		onValidateCopyOptionName: function(formView, field, value, params) {
			if (params.bSubset) {
				return;
			}

			var invalidCopyOptionNameFormat = false;
			var allCopyCategory = formView.m_formData.AllCopyCategory;

			if (allCopyCategory == xml.enumCreate(COPY_CATEGORY, "Optional") ||
				allCopyCategory == xml.enumCreate(COPY_CATEGORY, "Optional-Passive")) {
				invalidCopyOptionNameFormat = !value || value.length == 0 || value.indexOf(".") < 0;
			}
			else if (allCopyCategory == xml.enumCreate(COPY_CATEGORY, "Optional-FilteredTypes")) {
				invalidCopyOptionNameFormat = !value || value.length == 0;
			}

			if (invalidCopyOptionNameFormat) {
				throw new Error(locale.resource("ERROR_AN_INVALID_COPY_OPTION_NAME_FORMAT"));
			}
		},

		onValidateInitialValue: function(formView, field, value, params) {
			if (params.bSubset) {
				return;
			}

			var type = this.getFieldValue(reflection.ATTR_VALUETYPE);
			var format = this.getFieldValue(reflection.ATTR_FORMAT);

			if (type == xml.ATTR_ENUM) {
				if (!field.m_control.store.get(value)) {
					throw new Error(locale.resource("CustomAttribute_ErrorEnumInitial"));
				}
			}

			heading.validateValue(value, type, format);

			var validationExpression = this.getFieldValue(FIELD_VALIDATION_EXPRESSION);
			if (validationExpression) {
				var validationResult = validateValue(value, validationExpression);
				if (validationResult === false) {
					throw new Error(locale.resource("CustomAttribute_ErrorInitialValueDoesntFulfillValidationExpression"));
				}
			}
		},

		onValidateNodeName: function(formView, field, value, params) {
			if (value.length > 0 && !isNaN(value.substring(0, 1))) {
				throw new Error(locale.resource("AttributeNameCanNotStartWithDigit"));
			}

			if (params.bSubset) {
				return;
			}

			var nodeCA = formView.m_formData.m_source.getNode();
			if (nodeCA) {
				if (Locale.getConfigurableAttributeId(nodeCA) == value) {
					return;
				}
			}

			var nodeBO = formView.m_formData.m_source.getParent();
			var reflectionAttributeOwner = this.getFieldValue("ReflectionAttributeOwner") || nodeBO.RootClassName;
			this._validateAttributeIdAgainstDomainClass(reflectionAttributeOwner, value);

			// prevent the creation of two attributes that have matching uom names (ex: 'Name' and 'NameUOM')
			if (value.endsWith(UOM_SUFFIX)) {
				this._validateAttributeIdAgainstDomainClass(reflectionAttributeOwner, getUOMValueString(value));
				this._validateAttributeIdAgainstBusinessObjectsRecursively(nodeBO.BaseBusinessObjects, getUOMValueString(value));
			} else {
				this._validateAttributeIdAgainstDomainClass(reflectionAttributeOwner, getUOMConversionString(value));
				this._validateAttributeIdAgainstBusinessObjectsRecursively(nodeBO.BaseBusinessObjects, getUOMConversionString(value));
			}

			if (this.uomForm) {
				this._validateValueTypeOnBaseClassesRecursively(nodeBO.BaseBusinessObjects, getUOMConversionString(value), xml.ATTR_REF, CLASS_UOM_CONVERSION);
				this._validateValueTypeOnDerivedClasses(nodeBO, getUOMConversionString(value), xml.ATTR_REF, CLASS_UOM_CONVERSION);
			}
		},
		/**
		 * Populate the "Column Width" if this attribute was configured
		 * @param {Field} f
		 * @param {FormData} fd
		 * @return {boolean}
		 */
		onChangeNodeName: function(formView, field, value) {
			const defaultWidthValue = types.getColumnWidthByAttributeId(value);

			if (defaultWidthValue) {
				const defaultWidthEnum = xml.enumCreate(COLUMNWIDTH_TYPE, defaultWidthValue);
				this.setFieldValue(reflection.ATTR_COLUMN_WIDTH, defaultWidthEnum);
			}
		},

		onValidateValueType: function(formView, field, value, params) {
			if (params.bSubset) {
				return;
			}

			var nodeBO = formView.m_formData.m_source.getParent();
			var name = this.getFieldValue(xml.ATTR_NAME);
			var targetClassName = this.getFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME);
			this._validateValueTypeOnBaseClassesRecursively(nodeBO.BaseBusinessObjects, name, value, targetClassName);
			this._validateValueTypeOnDerivedClasses(nodeBO, name, value, targetClassName);
		},

		/**
		 * make sure the attribute with 'attrId' doesn't exist on all classes
		 * of domain class where 'className' belong
		 * @param {String} className 
		 * @param {String} attrId 
		 */
		_validateAttributeIdAgainstDomainClass(className, attrId) {
			const reflectionClassesForAttributeIdValidation = getReflectionClassesForAttributeIdValidation(className);

			// gather all BO nodes in cache
			const setUrls = new Set();

			reflectionClassesForAttributeIdValidation.forEach((rc) => {
				const boUrl = rc.getBusinessObject();

				if (boUrl) {
					setUrls.add(boUrl);
				}
			}, this);

			const bos = this.cacheView.getNodes([...setUrls]);

			// validate against Business Object if exists for reflection class or reflection class itself
			bos.forEach((bo) => {
				validateAttributeIdAgainstBusinessObject(bo, attrId);
			}, this);

			// validate against the Business Object we are on
			var nodeBO = this.cacheView.getNode(this.getSourceView().urlRoot);
			validateAttributeIdAgainstBusinessObject(nodeBO, attrId);
		},

		/**
		 * @param {String[]} boUrls 
		 * @param {String} attrId 
		 */
		_validateAttributeIdAgainstBusinessObjectsRecursively(boUrls, attrId) {
			const bos = this.cacheView.getNodes(boUrls);

			bos.forEach((bo) => {
				validateAttributeIdAgainstBusinessObject(bo, attrId);
				this._validateAttributeIdAgainstBusinessObjectsRecursively(bo.BaseBusinessObjects, attrId);
			});
		},

		/**
		 * @param {String[]} boUrls 
		 * @param {String} name 
		 * @param {String} type 
		 * @param {String} targetClassName 
		 */
		_validateValueTypeOnBaseClassesRecursively(boUrls, name, type, targetClassName) {
			const bos = this.cacheView.getNodes(boUrls);

			bos.forEach((bo) => {
				this._validateValueTypeAgainstBO(bo, "CustomAttribute_ErrorTypeDifferentFromBase", name, type, targetClassName);
				this._validateValueTypeOnBaseClassesRecursively(bo.BaseBusinessObjects, name, type, targetClassName);
			});
		},

		/**
		 * @param {Node} currentBO 
		 * @param {String} name 
		 * @param {String} type 
		 * @param {String} targetClassName 
		 */
		_validateValueTypeOnDerivedClasses(currentBO, name, type, targetClassName) {
			const bos = getDerivedBusinessObjects(currentBO, this.cacheView);

			bos.forEach((bo) => {
				this._validateValueTypeAgainstBO(bo, "CustomAttribute_ErrorTypeDifferentFromDerived", name, type, targetClassName);
			});
		},

		_validateValueTypeAgainstBO: function(/*Node*/bo, /*String*/errorMessageResource, /*String*/name, /*String*/type, /*String*/targetClassName) {
			// summary: if the attribute with the same name exists on the passed BO and the type of attribute is different, throw an exception
			var ca = getConfigurableAttributeFromBOByName(bo, name, this.cacheView);
			if (!ca)
				return;

			// compare types
			var typeOfExistingAttr = ca[reflection.ATTR_VALUETYPE];
			if (type != typeOfExistingAttr)
				throw new Error(locale.resource(errorMessageResource, [typeOfExistingAttr, name, locale.getNodeName(bo, this.cacheView)]));

			// compare target classes
			var bCorrectTargetClassName = true;
			var classNames = null;
			var existingTargetClassName = ca[BusinessObject.ATTR_TARGET_CLASS_NAME];
			if (existingTargetClassName) {
				bCorrectTargetClassName = (targetClassName == existingTargetClassName);
				classNames = [existingTargetClassName];
			}
			// DE27610: can't change target class on BuiltIn attributes. So removed the validation for it.

			if (!bCorrectTargetClassName) {
				classNames = _.map(classNames, function(clsName) {return getDisplayableClassName(clsName)});
				throw new Error(locale.resource("CustomAttribute_ErrorTypeDifferentTargetClass", [classNames.join(", "), name, locale.getNodeName(bo, this.cacheView)]));
			}
		},

		/**
		 * Conditionally draw the depends on field and populate its store.
		 */
		_drawDependsOn: function() {
			const {boURL} = this;
			const fieldDependsOn = this.getField(this.DEPENDS_ON);
			this.setFieldHidden(fieldDependsOn, true);
			this.setFieldValue("DependsOnPath", "");
			const fieldEnum = this.getField(reflection.TYPE_ENUM);
			const enumId = fieldEnum.getValue();
			const control = fieldEnum.m_control;
			const valueType = this.getFieldValue(reflection.ATTR_VALUETYPE);

			if (control && enumId && valueType.includes("enum")) {
				const bDependsOn = !!this.dependsOnEnumListName;
				const value = fieldDependsOn.getValue();

				if (fieldDependsOn.m_control) {
					this.setFieldHidden(this.DEPENDS_ON, !bDependsOn);
					setTimeout(() => this._disableInitialValue(value), 0);
				}
			} else if (this.getFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME) === "LookupItem") {
				const value = this.getFieldValue("Subtype");
				if (!xml.isValidURL(value)) {
					return;
				}
				const {cacheView} = this;
				const ctx = this.getContext();
				cacheView.getNodeDependencies([value], [
					"Child:SetupSettings",
				]).then(() => {
					const valueNode = cacheView.getNode(value);
					const settingsNode = cacheView.getNode(valueNode.SetupSettings);
					if (xml.isValidURL(settingsNode.DependsOnType)) {
						const q = new Query().RefAttr("Subtype", xml.OP_EQUALS, settingsNode.DependsOnType);
						const rel = lang.mixin(new Relationship(), {
							m_id: "ConfigurableAttributes",
							m_typeNode: "CustomAttribute",
							m_query: q,
							m_url: boURL,
						});
						const storeOptions = csiStore.namesOptionsByRelationship(ctx, rel);
						storeOptions.data = [csiStore.nameObject("")];
						const storeDependsOn = new Names(storeOptions);
						this.setFieldStore(fieldDependsOn, storeDependsOn);
						this.setFieldHidden(fieldDependsOn, false);
						this.setFieldValue("AutofillDependentEnum", settingsNode.IsAutofillDependent);
					}
				});
			}
		},

		_drawFlags: function(eControl, nBit) {
			var node = this.getFormSourceNode();
			var flags = node ? node[BusinessObject.ATTR_ATTRIBUTE_FLAGS] : null;
			eControl.set("value", xml.valueHasFlag(flags, nBit));
		},

		_drawRFlagRestrictDragEdits: function() {
			var fieldRestrictDragEdits = this.getField(BusinessObject.ATTR_DRAG_EDIT_DISABLED);
			var tr = this.getFieldRow(fieldRestrictDragEdits);
			var fieldReadOnly = this.getFieldValue(FIELD_FLAG_READ_ONLY);

			html.setVisible(tr, !fieldReadOnly);
		},

		_postActionPopupEnumerations: function(ac) {
			// summary: refresh enum lists
			const field = this.getField(reflection.TYPE_ENUM);
			const fd = ac.popupView.defView.m_formData;
			const enumId = fd.getSelectedNode().$Name;

			this._onDependency(field);
			this.setFieldValue(field, enumId);
		},

		_postView: function() {
			// summary: force dialog to recalculate size and position
			this.publishDomChange();
			this.inherited(arguments);
		},

		_updateInitialValueEnumValue: function(/*String?*/value) {
			// summary: populate select control with values from server so it will get most up-to-date values rather than cached values
			var w = this.getFieldControl(reflection.ATTR_VALUE);
			var enumId = this.getFieldValue(reflection.TYPE_ENUM);

			value = xml.enumGetText(value) ? value : xml.enumCreate(enumId);
			w.set("store", enums.getServerStore(enumId, value));
			w.set("value", value);
		},

		_disableInitialValue: function(/*String?*/value) {
			// summary: disable initial value field if depends on field has value
			var w = this.getFieldControl(reflection.ATTR_VALUE);
			var fieldDependsOn = this.getField("DependsOn");
			var tr = this.getFieldRow(fieldDependsOn);
			var dependsOn = this.getFieldValue("DependsOn");
			var bDisabled = false;

			if (html.isVisible(tr) && dependsOn !== csi.EMPTY_URL) {
				bDisabled = true;
			}

			w.set("disabled", bDisabled);
		},

		_isPublished: function() {
			// summary: if a reflection attribute has been assigned, then the attribute is published
			var node = this.getViewNode();
			return node && !!node.ReflectionAttributeId;
		},

		_resolveView: function() {
			// summary: adjust form based on attribute type
			var node = this.getFormSourceNode();
			var bBuiltIn = isBuiltInAttribute(node);
			var dependsOnControl = this.getField(this.DEPENDS_ON).getControlName();
			var self = this;

			// In case the Destination has no Subtype, use LookupItemSubtype to avoid relationship warning
			var subtypeClass = "LookupItemSubtype";
			if (node && node.TargetClassName in DESTINATION_CLASS_NAMES_WITH_SUBTYPE) {
				subtypeClass = DESTINATION_CLASS_NAMES_WITH_SUBTYPE[node.TargetClassName];
			}

			// if on a copy, make the Value Type read-only
			if (this.isCopy()) {
				this.setFieldDisabled(reflection.ATTR_VALUETYPE, true);
			}

			// insert class field
			const targetClassReadOnly = xml.nodeAttributeHasFlag(node, BusinessObject.ATTR_TARGET_CLASS_NAME, xml.ATTR_FLAGS_READONLY);
			this.insertField(lang.mixin(new Field(), {
				bCombineWithPrevious: true,
				bForceEditable: !bBuiltIn && !targetClassReadOnly,
				bHideLabelForCombinedField: true,
				m_bRequired: false,
				m_id: BusinessObject.ATTR_TARGET_CLASS_NAME,
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_onChange: "onChangeTargetClass"
			}), dependsOnControl);

			// insert subtype field
			this.insertField(lang.mixin(new Field(), {
				bCombineWithPrevious: true,
				bForceEditable: false,
				bHideLabelForCombinedField: true,
				m_id: reflection.ATTR_SUBTYPE,
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_onChange: "onChangeSubtype",
				m_relationship: lang.mixin(new Relationship(), {
					m_typeNode: subtypeClass,
					m_query: new Query().Attr("Active", xml.OP_EQUALS, true, "Child:SetupSettings")
				})
			}), dependsOnControl);

			// insert enum field
			this.insertField(lang.mixin(new Field(), {
				bCombineWithPrevious: true,
				bHideLabelForCombinedField: true,
				m_bRequired: false,
				m_label: reflection.TYPE_ENUM,
				m_typeControl: def.FIELD_FILTERING_SELECT,
				m_onChange: "onChangeEnum",
				m_onDraw: function(formView, field, value, eParent, eControl) {
					if (eParent.children.length < 2) {
						var ctx = self.getFormContext();
						var button = self.getAction("PopupEnumerations").toHtmlWithAccess(ctx, eParent);

						style.set(button, {
							marginLeft: "5px",
							padding: "0",
							position: "relative",
							top: "1px",
							width: "auto"
						});
					}
				},
				m_format: types.FORMAT_SELECTION,
				m_relationship: lang.mixin(new Relationship(), {
					m_typeNode: reflection.TYPE_ENUM,
					m_query: new Query().Attr("Internal", xml.OP_EQUALS, false)
				})
			}), dependsOnControl);

			// add action to popup enumeration dialog
			var a = lang.mixin(new Popup(), {
				m_id: "PopupEnumerations",
				m_typeControl: def.ACTION_BUTTON,
				classController: _PopupController,
				m_postAction: lang.hitch(this, "_postActionPopupEnumerations"),
				m_viewRef: lang.mixin(new ViewRef(), {
					m_url: "centric://SITEADMIN/_CS_Site",
					m_id: "Enumerations"
				})
			});
			a.resolve();
			this.defView._addViewAction(a);

			this.inherited(arguments);

			// setup default/fixed value for fields
			var fd = this.defView.m_formData;
			if (!fd)
				return;

			var type = node ? node[reflection.ATTR_VALUETYPE] : null;

			// set up default/fixed value for enum field
			if (enums.isEnumBaseType(type)) {
				// the enumId is stored in format, so we have to get it from format field on node
				var enumId = xml.enumGetId(node[reflection.ATTR_FORMAT]);

				if (bBuiltIn && node) {
					this.addFixedValue(reflection.TYPE_ENUM, enumId);
				} else {
					this.addDefaultValue(reflection.TYPE_ENUM, enumId);
				}
			}

			// set default value for type - not editable for built-in and published attributes
			if (node && (bBuiltIn || node.AppliedOn)) {
				this.addFixedValue(reflection.ATTR_VALUETYPE, type);
			} else {
				this.addDefaultValue(reflection.ATTR_VALUETYPE, xml.ATTR_STRING);
				this.addDefaultValue(reflection.ATTR_COLUMN_WIDTH, xml.enumCreate(COLUMNWIDTH_TYPE, types.SMALL));
			}

			// disable owner attribute editing for published or built-in attributes
			if (node && (bBuiltIn || node.AppliedOn)) {
				this.setFieldDisabled(reflection.ATTR_REFLECTION_ATTR_OWNER, true);
			}

			// disable editing of string format if attribute is built-in
			if (bBuiltIn && (type == xml.ATTR_STRING || type == xml.ATTR_STRINGMAP)) {
				this.addFixedValue(reflection.ATTR_FORMAT, node[reflection.ATTR_FORMAT]);
			}

			// set defaults for copy fields
			this.addDefaultValue(COPY_ROOT_TYPE, COPY_CATEGORY_ALL);

			fd.InitialAllCopyCategory = fd.AllCopyCategory =
				(node ? node[reflection.ATTR_COPY_CATEGORIES].All : null) || xml.enumCreate(COPY_CATEGORY);

			if (node && node[reflection.ATTR_COPY_CATEGORIES_LOCKED]) {
				this.addFixedValue(COPY_CATEGORY, fd.AllCopyCategory);
			} else {
				this.addDefaultValue(COPY_CATEGORY, fd.AllCopyCategory);
			}

			var site = resolver.resolveNode(reflection.URL_SITE, true);
			if (site.RestApiBlockChanges && node) {
				this.addFixedValue(BusinessObject.ATTR_REST_API_EXPOSED, node.RestApiExposed);
			}

			if (this.isCopy()) {
				const fromNode = this.cacheView.getNode(this.urlRoot);
				this.addFixedValue(xml.ATTR_NAME, fromNode.ReflectionAttributeId);

				// see if the source is on a Revision class - if so, make the owner the revision.
				const reflectionAttributeOwner = this.getSourceRootNode().AttributeClassName;
				if (this.isAttributeClassDifferentFromRoot(this.getSourceRootNode())) {
					const isOnRevisionClass = fromNode.ReflectionAttributeOwner.endsWith("Revision");
					this.addFixedValue(reflection.ATTR_REFLECTION_ATTR_OWNER, isOnRevisionClass ? reflectionAttributeOwner : this.getSourceRootNode().RootClassName);
				} else {
					this.addFixedValue(reflection.ATTR_REFLECTION_ATTR_OWNER, reflectionAttributeOwner);
				}

				// see if the type is a ref - if so, look up the target class if we are extending a built-in attribute
				if (fromNode.$Type == "BuildInAttribute" && xml.attributeTypeIsRef(fromNode[reflection.ATTR_VALUETYPE])) {
					const targetClasses = reflection.getAttributeByClassNameAndId(fromNode.ReflectionAttributeOwner, fromNode.ReflectionAttributeId).destinations;
					if (targetClasses.length == 1) { // did we nail a unique target?
						this.addFixedValue(BusinessObject.ATTR_TARGET_CLASS_NAME, targetClasses[0].type);
					}
				}

				// see if the type is an enum - if so, look up the target class if we are extending a built-in attribute
				if (fromNode[reflection.ATTR_VALUETYPE].startsWith(xml.ATTR_ENUM)) {
					const targetEnumType = reflection.getAttributeByClassNameAndId(fromNode.ReflectionAttributeOwner, fromNode.ReflectionAttributeId).getFormat();
					this.addFixedValue(reflection.TYPE_ENUM, targetEnumType);
				}
			}

			// Check if this form is UOM
			const uomNodeURL = getUOMConversionString(node?.$URL);
			// Check if UOM node exists
			if (resolver.resolveNode(uomNodeURL, true, this.cacheView)) {
				this.uomForm = true;
				this._enableUOM();
			}
		},

		_updateAutofillVisibility: function(/*Boolean*/flag) {
			var tr = this.getFieldRow("AutofillDependentEnum");
			html.setVisible(tr, flag);
		},

		_updateFieldVisibility: function(/*Field?*/field) {
			// summary: hide some fields for certain types and formats
			this.inherited(arguments);

			const type = this.getFieldValue(reflection.ATTR_VALUETYPE);
			const fieldValue = this.getField(reflection.ATTR_VALUE);
			const dependsOn = this.getFieldValue(reflection.ATTR_DEPENDS_ON);
			this._updateAutofillVisibility(xml.isValidURL(dependsOn));
			this._updateFormatVisibility();
			this._enableUOM();

			if (fieldValue) {
				const format = this.getFieldValue(reflection.ATTR_FORMAT);
				const bHide = xml.attributeTypeIsContainer(type) || array.some(NO_VALUE, function(obj) {
					return (type == obj.type) && (!obj.format || (format == obj.format));
				});

				this.setFieldHidden(fieldValue, !fieldValue.m_control || bHide);
			}

			const bRef = xml.attributeTypeIsRef(type);
			const bEnum = enums.isEnumBaseType(type);
			const bSubtype = bRef && hasSubtype(this.getFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME, true));

			this.setFieldDisabled(reflection.ATTR_SUBTYPE, !bSubtype);

			this.setFieldHidden(reflection.ATTR_FORMAT, bRef);
			this.setFieldHidden(FIELD_VALIDATION_EXPRESSION, bRef);
			this.setFieldHidden(FIELD_LOCALE_VALIDATION, bRef);
			this.setFieldHidden(reflection.TYPE_ENUM, !bEnum);
			this.setFieldHidden(BusinessObject.ATTR_TARGET_CLASS_NAME, !bRef);
			this.setFieldHidden(reflection.ATTR_SUBTYPE, !bSubtype);

			this.setFieldRequired(reflection.TYPE_ENUM, bEnum);
			this.setFieldRequired(BusinessObject.ATTR_TARGET_CLASS_NAME, bRef);

			this._updateFieldVisibilityForUOM();

			return true;
		},

		/**
		 * If UOM boolean is selected, modify fields to show on form.  Called on form initialization as well
		 */
		_updateFieldVisibilityForUOM() {
			const uomForm = this.uomForm;
			this.setFieldHidden(FIELD_UOM_TYPE, !uomForm);
			this.setFieldHidden(FIELD_UNIT_OF_MEASURE, !uomForm);
			this.setFieldHidden(FIELD_FLAG_READ_ONLY, uomForm);
			this.setFieldHidden(BusinessObject.ATTR_EXP_ONCLIENT_ENABLED, uomForm);
			this.setFieldHidden(reflection.ATTR_HIDDEN, uomForm);
			this.setFieldDisabled(FIELD_REQUIRED, uomForm);
			
			if (uomForm && this.isEdit()) {
				// extract the uomTypeName and uomName from the conversionURL
				const conversionURL = JSON.parse(this.cacheView.getNode(getUOMConversionString(this.urlRoot))[reflection.ATTR_VALUE]);
				const {uomName, uomTypeName} = getUOMNameAndUOMTypeNameFromConversionCNL(conversionURL);

				this.setFieldValue(FIELD_UOM_TYPE, generateUOMTypeURL(uomTypeName));
				this.setFieldValue(FIELD_UNIT_OF_MEASURE, generateUnitOfMeasureURL(uomTypeName, uomName));
				this.setFieldValue(FIELD_UNIT_OF_MEASURE_ENABLED, true);
				this.setFieldDisabled(FIELD_UOM_TYPE, true);
				this.setFieldDisabled(FIELD_UNIT_OF_MEASURE, true);
				this.setFieldDisabled(FIELD_UNIT_OF_MEASURE_ENABLED, true);
				this.setFieldDisabled(reflection.ATTR_VALUETYPE, true);
			}
		},

		/**
		 * Update initial value field based on selected type and format
		 * @param {Boolean} bChanged - indicates the initial value has to change because the type/enum changed
		 */
		_updateInitialValue(bChanged) {
			var f = this.getField(reflection.ATTR_VALUE);
			var ctx = this.getFormContext();

			if (f) {
				var type = this.getFieldValue(reflection.ATTR_VALUETYPE);
				var format = this.getFieldValue(reflection.ATTR_FORMAT) || csi.EMPTY_STRING;
				var defaultValue = xml.attributeTypeGetDefaultValue(type, format);

				f.setType(type, format);
				f.heading.m_format = format;

				if (bChanged) {
					// apply value on node so the previous value won't stay on the Initial Value drop down
					var node = ctx.getNode();
					node[reflection.ATTR_VALUE] = _.isObject(defaultValue) ? "" : JSON.stringify(defaultValue);
				}

				// rebuild field to reflect new type/format
				var tr = this.getFieldRow(f);
				if (tr) {
					html.destroyChildren(tr);
					f.m_control = null;
					this._buildField(ctx, f, tr);
				}
			}
		},

		getAttributeValueFromField: function(/*Field*/field, /*String*/attrId, /*Node?*/newNode) {
			// stringify the value for 'Initial Value' attribute if it is coming from the control directly
			let value = this.inherited(arguments);
			if (attrId == reflection.ATTR_VALUE && field.m_control) {
				value = JSON.stringify(value);
			}
			return value;
		},

		_updateTargetClassAndSubtype: function() {
			// reset TargetClassName and Subtype if necessary
			var type = this.getFieldValue(reflection.ATTR_VALUETYPE);
			var targetClassName = this.getFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME);

			if (!xml.attributeTypeIsRef(type)) {
				targetClassName = "";
				this.setFieldValue(BusinessObject.ATTR_TARGET_CLASS_NAME, targetClassName);
			}

			var subtypeClass = DESTINATION_CLASS_NAMES_WITH_SUBTYPE[targetClassName];

			// Reset field value
			this.setFieldValue(reflection.ATTR_SUBTYPE, csi.EMPTY_URL);

			if (null != subtypeClass) {
				var field = this.getField(reflection.ATTR_SUBTYPE);

				// Change Subtype Class to select from
				field.m_relationship.m_typeNode = subtypeClass;

				// Reset Data Store
				this.setFieldStore(field, this.getFieldStore(field));
			}
		},

		onChangeUOMEnabled() {
			this.uomForm = this.getFieldValue(FIELD_UNIT_OF_MEASURE_ENABLED);
			this.setFieldValue(FIELD_REQUIRED, this.uomForm);
		},

		/**
		 * Reset UOM fields on Save and New
		 */
		resetForm() {
			this.uomForm = false;
			this.inherited(arguments);
		},

		_updateFormatVisibility: function() {
			// summary: checks if the type is enum or a container of enum, then hides format field, otherwise shows it.
			var fieldFormat = this.getField(reflection.ATTR_FORMAT);
			var formatRow = this.getFieldRow(fieldFormat);
			var fieldType = this.getField(reflection.ATTR_VALUETYPE);

			html.setVisible(formatRow, !enums.isEnumBaseType(fieldType.getValue()));
		},

		_updateViableMatrixVisibility: function() {
			return; // nothing to do since matrix flag is hidden for 6.0

			/*
			// summary: We should only show Viable Matrix flag if Value Type is a Ref container AND attribute hidden flag is false
			var	valueType = this.getFieldValue(reflection.ATTR_VALUETYPE);
			var baseType = xml.attributeTypeGetBase(valueType);
			var refContainer = baseType && baseType == xml.ATTR_REF;
			var flagHidden = this.getFieldValue(reflection.ATTR_HIDDEN);

			var disableField = flagHidden || !refContainer;
			var viableMatrixFlag = this.getField(reflection.ATTR_VIABLE_MATRIX);
			var tr = this.getFieldRow(viableMatrixFlag);

			if (disableField) {
				viableMatrixFlag.m_control.set("value", false);
			}
			html.setVisible(tr, !disableField);
			*/
		},
		/**
		 * Decides whether we should build the field or not. We don't allow the field to be built if it's hidden or
		 * its type is a container and it's not multi-select.
		 * @param {Field} field
		 * @return {boolean}
		 */
		_isFieldBuildable: function(field) {
			return this.inherited(arguments) && (!xml.attributeTypeIsContainer(field.getType()) || field.m_typeControl == def.FIELD_MULTI_SELECT);
		}
	});
});
